import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getAllFarmerComplaints, getAllSupportEmails } from '../../api/web';

const Support = () => {
    const [complaints, setComplaints] = useState([]);
    const [supportEmails, setSupportEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('complaints');

    const loadData = () => {
        setLoading(true);
        Promise.all([getAllFarmerComplaints(), getAllSupportEmails()])
            .then(([complaintsRes, emailsRes]) => {
                const complaintsList = complaintsRes.payload?.results || complaintsRes.payload || [];
                const emailsList = emailsRes.payload?.results || emailsRes.payload || [];
                setComplaints(Array.isArray(complaintsList) ? complaintsList : []);
                setSupportEmails(Array.isArray(emailsList) ? emailsList : []);
            })
            .catch(() => {
                toast.error('Failed to load support data', { position: 'top-right', autoClose: 3000 });
                setComplaints([]);
                setSupportEmails([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadData();
    }, []);

    const locationStr = (c) => {
        const loc = c.farm?.nearest_town?.name || c.complaint_by?.town?.name || c.farm?.province?.name || '';
        return loc || '—';
    };

    const dateStr = (item) => {
        const d = item.date_created || item.created_at;
        return d ? moment(d).format('Do MMM, YYYY HH:mm') : '—';
    };

    const complaintStatusBadge = (status) => {
        const map = {
            pending: { cls: 'bg-warning text-dark', style: {} },
            addressed: { cls: 'bg-success text-white', style: {} }
        };
        const key = (status || '').toLowerCase();
        const c = map[key] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    const severityBadge = (severity) => {
        const map = {
            low: { cls: 'bg-secondary text-white', style: {} },
            medium: { cls: 'bg-info text-white', style: {} },
            high: { cls: 'bg-warning text-dark', style: {} },
            extreme: { cls: 'bg-danger text-white', style: {} }
        };
        const key = (severity || '').toLowerCase();
        const c = map[key] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{severity || '—'}</span>;
    };

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Support</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Support</h4>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadData} disabled={loading}>
                        <i className="icon ion-md-refresh mg-r-5"></i> Refresh
                    </button>
                </div>

                <ul className="nav nav-tabs nav-tabs-line mg-b-20">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'complaints' ? 'active' : ''}`}
                            onClick={() => setActiveTab('complaints')}
                        >
                            Complaints ({complaints.length})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'support' ? 'active' : ''}`}
                            onClick={() => setActiveTab('support')}
                        >
                            Support Emails ({supportEmails.length})
                        </button>
                    </li>
                </ul>

                {loading ? (
                    <div className="text-center pd-y-40">
                        <div className="spinner-border text-primary" role="status" />
                        <p className="mg-t-10 tx-12 tx-color-03">Loading...</p>
                    </div>
                ) : (
                    <>
                        {activeTab === 'complaints' && (
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="mg-b-0">Farmer Complaints</h6>
                                </div>
                                <div className="card-body table-responsive">
                                    {complaints.length === 0 ? (
                                        <p className="tx-color-03 mg-b-0">No complaints.</p>
                                    ) : (
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Message</th>
                                                    <th>Severity</th>
                                                    <th>Status</th>
                                                    <th>Location</th>
                                                    <th>Complainant</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {complaints.map((c) => (
                                                    <tr key={c.id}>
                                                        <td>{c.complaint_type || '—'}</td>
                                                        <td className="wd-20p">{c.message || '—'}</td>
                                                        <td>{severityBadge(c.severity)}</td>
                                                        <td>{complaintStatusBadge(c.query_status || c.status)}</td>
                                                        <td>{locationStr(c)}</td>
                                                        <td>{c.complaint_by?.username || c.complaint_by?.first_name || '—'}</td>
                                                        <td>{dateStr(c)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'support' && (
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="mg-b-0">Support Emails</h6>
                                </div>
                                <div className="card-body table-responsive">
                                    {supportEmails.length === 0 ? (
                                        <p className="tx-color-03 mg-b-0">No support emails.</p>
                                    ) : (
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Ticket</th>
                                                    <th>Subject</th>
                                                    <th>Sender</th>
                                                    <th>Message</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {supportEmails.map((e) => (
                                                    <tr key={e.id}>
                                                        <td>{e.support_ticket || '—'}</td>
                                                        <td>{e.subject || '—'}</td>
                                                        <td>{e.sender_email || [e.first_name, e.last_name].filter(Boolean).join(' ') || '—'}</td>
                                                        <td className="wd-30p">{e.message ? (e.message.length > 80 ? e.message.slice(0, 80) + '…' : e.message) : '—'}</td>
                                                        <td>{dateStr(e)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Support;
